import { Clipboard } from '@angular/cdk/clipboard';
import { isPlatformBrowser } from '@angular/common';
import {
    Inject,
    Injectable,
    PLATFORM_ID,
    Renderer2,
    RendererFactory2,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import {
    DialogService,
    DynamicDialogConfig,
    DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { BehaviorSubject, Subject } from 'rxjs';

import { environment } from '../../environments/environment';


export interface IconsConfig {
    title?: string;
    path: string;
    tag?: string;
    field?: string;
    source?: string;
    icons?: string[];
}

@Injectable({
    providedIn: 'root',
})
export class UIService {

    iconIds = new BehaviorSubject<string[]>([]);
    icons: IconsConfig[] = [
        { path: 'assets/icons/icons.svg', tag: 'symbol', field: 'id' },
    ];

    onPaste$ = new Subject<ClipboardEvent>();

    os: 'Windows' | 'MacOS' | string = 'Unknown';
    ctrlKey: 'Ctrl' | '⌘' = 'Ctrl';

    isUserInteracted = false;

    private renderer: Renderer2;

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private rendererFactory: RendererFactory2,
        private messageService: MessageService,
        private clipboard: Clipboard,
        private dialogService: DialogService,
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);

        // Load icons
        this.icons.forEach(cfg => {
            this.fetchIcons(cfg.path, cfg.tag, cfg.field).then(list => {
                this.iconIds.next([...this.iconIds.value, ...list]);
            });
        });

        // OS detection
        if (this.isBrowser) {
            this.os = /mac os/i.test(navigator.userAgent)
                ? 'MacOS'
                : /windows/i.test(navigator.userAgent)
                    ? 'Windows'
                    : 'Unknown';

            this.ctrlKey = this.os === 'MacOS' ? '⌘' : 'Ctrl';
        }

        // Restore known features
        if (this.isBrowser) {
            const saved = localStorage.getItem('known-features');
            if (saved) {
                this._knownFeatures.next(JSON.parse(saved));
            }
        }
    }

    private get isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    // ===== ENV =====
    get isDev(): boolean {
        return environment.env !== 'production';
    }

    get env(): string {
        return environment.env;
    }

    // ===== INITIALIZATION =====
    private _init = { flags: false, user: false, tabs: false };
    private _initialized = new BehaviorSubject(false);

    get initialized(): boolean {
        return this._initialized.value;
    }

    set initialized(val: boolean) {
        this._initialized.next(val);
    }

    markInitialized(key: 'flags' | 'user' | 'tabs') {
        this._init[key] = true;
        if (Object.values(this._init).every(x => x)) {
            this.initialized = true;
        }
    }

    // ===== TABS =====
    private _expandedTabs = new BehaviorSubject<string[]>([]);

    get expandedTabs(): string[] {
        return this._expandedTabs.value;
    }

    set expandedTabs(val: string[]) {
        this._expandedTabs.next(val);
        if (this.isBrowser) {
            localStorage.setItem('expanded-tabs', JSON.stringify(val));
        }
    }

    expandTab(tab: string) {
        if (!this.expandedTabs.includes(tab)) {
            this.expandedTabs = [...this.expandedTabs, tab];
        }
    }

    toggleTab(tab: string) {
        if (this.expandedTabs.includes(tab)) {
            this.expandedTabs = this.expandedTabs.filter(x => x !== tab);
        } else {
            this.expandedTabs = [...this.expandedTabs, tab];
        }
    }

    isTabExpanded(tab: string): boolean {
        return this.expandedTabs.includes(tab);
    }

    restoreExpandedTabs(): Promise<void> {
        return new Promise(resolve => {
            if (!this.isBrowser) {return resolve();}
            const raw = localStorage.getItem('expanded-tabs');
            this.expandedTabs = raw ? JSON.parse(raw) : [];
            resolve();
        });
    }

    // ===== FEATURE FLAGS =====
    private _featureFlags = new BehaviorSubject<string[]>([]);

    get featureFlags(): string[] {
        return this._featureFlags.value;
    }

    isEnabled(flag: string): boolean {
        return this.featureFlags.includes(flag);
    }

    set featureFlags(val: string[]) {
        this._featureFlags.next(val);
        if (this.isBrowser) {
            localStorage.setItem('feature-flags', JSON.stringify(val));
        }
    }

    setFeatureFlag(flag: string, enabled = true) {
        if (enabled && !this.featureFlags.includes(flag)) {
            this.featureFlags = [...this.featureFlags, flag];
        }
        if (!enabled && this.featureFlags.includes(flag)) {
            this.featureFlags = this.featureFlags.filter(x => x !== flag);
        }
    }

    restoreFeatureFlags(): Promise<void> {
        return new Promise(resolve => {
            if (!this.isBrowser) {return resolve();}
            const raw = localStorage.getItem('feature-flags');
            this.featureFlags = raw ? JSON.parse(raw) : [];
            resolve();
        });
    }

    // ===== LOADING =====
    private _loading = new BehaviorSubject(false);

    get loading(): boolean {
        return this._loading.value;
    }

    set loading(val: boolean) {
        this._loading.next(val);
    }

    loading$ = this._loading.asObservable();

    // ===== BLUR =====
    private _blurred = new BehaviorSubject(false);

    get blurred(): boolean {
        return this._blurred.value;
    }

    set blurred(val: boolean) {
        this._blurred.next(val);

        if (this.isBrowser) {
            this.renderer.removeClass(document.body, 'blurred');
            if (val) {this.renderer.addClass(document.body, 'blurred');}
        }
    }

    blurred$ = this._blurred.asObservable();

    // ===== FEATURE HINTS =====
    private _knownFeatures = new BehaviorSubject<string[]>([]);
    private _shownFeature = new BehaviorSubject<string | null>(null);
    private _featuresQueue = new BehaviorSubject<string[]>([]);

    shownFeature$ = this._shownFeature.asObservable();

    isKnown(f: string): boolean {
        return this._knownFeatures.value.includes(f);
    }

    addFeature(f: string) {
        if (this.isKnown(f)) {return;}

        if (!this._featuresQueue.value.includes(f)) {
            this._featuresQueue.next([...this._featuresQueue.value, f]);
        }

        if (!this._shownFeature.value) {
            setTimeout(() => {
                const q = this._featuresQueue.value;
                this._shownFeature.next(q.shift() ?? null);
            }, 3000);
        }
    }

    closeFeature(f: string) {
        if (!this.isKnown(f)) {
            this._knownFeatures.next([...this._knownFeatures.value, f]);
            if (this.isBrowser) {
                localStorage.setItem(
                    'known-features',
                    JSON.stringify(this._knownFeatures.value),
                );
            }
        }

        setTimeout(() => {
            const q = this._featuresQueue.value;
            this._shownFeature.next(q.shift() ?? null);
        }, 3000);
    }

    // ===== TOASTS =====
    toast(
        message: string,
        severity: 'success' | 'info' | 'warn' | 'error' = 'info',
        extra: Record<string, any> = {},
    ) {
        extra['life'] ??= 5000;
        const summary = severity === 'warn'
            ? 'Warning'
            : severity[0].toUpperCase() + severity.substring(1);

        this.messageService.add({
            severity,
            summary,
            detail: message,
            ...extra,
        });
    }

    clearToast(key?: string) {
        this.messageService.clear(key);
    }

    // ===== CLIPBOARD =====
    copy(value: string, toast?: string) {
        if (!value) {return;}

        this.clipboard.copy(value);

        if (toast) {
            this.toast(toast, 'success', { life: 2000 });
            return;
        }

        const preview = value.length > 100 ? value.slice(0, 100) + '...' : value;
        this.toast(`Copied '<strong>${preview}</strong>'`, 'success', {
            life: 2000,
        });
    }

    // ===== DIALOGS =====
    openDialog(component: any, data: any = {}, cfg: DynamicDialogConfig = {}): DynamicDialogRef {
        return this.dialogService.open(component, {
            closeOnEscape: true,
            closable: true,
            maximizable: true,
            modal: true,
            resizable: false,
            draggable: false,
            baseZIndex: 5,
            position: 'center',
            appendTo: 'body',
            duplicate: true,
            data,
            ...cfg,
        });
    }

    maximizeDialog(ref: DynamicDialogRef) {
        this.dialogService.getInstance(ref)?.maximize();
    }

    // ===== UTILS =====
    isYouTubeLink(url: string): boolean {
        return /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\//.test(url);
    }

    constructYouTubeEmbedUrl(url: string): string {
        if (!this.isYouTubeLink(url)) {return '';}

        const videoId = url.includes('youtube.com')
            ? url.split('v=')[1]
            : url.split('youtu.be/')[1];

        return `https://www.youtube.com/embed/${videoId}`;
    }

    setFavicon(name = 'favicon') {
        if (!this.isBrowser) {return;}

        const curr = document.querySelector('link[rel="icon"]');
        if (curr) {curr.remove();}

        const link = this.renderer.createElement('link');
        link.rel = 'icon';
        link.href = `${name}.ico`;

        this.renderer.appendChild(document.head, link);
    }

    async fetchIcons(path: string, tag = 'symbol', field = 'id'): Promise<string[]> {
        if (!this.isBrowser) {return [];}

        const text = await fetch(path).then(r => r.text());
        const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
        const symbols = doc.getElementsByTagName(tag);

        const list: string[] = [];

        for (let i = 0; i < symbols.length; i++) {
            const id = symbols.item(i)?.getAttribute(field);
            if (id) {list.push(id);}
        }

        return [...new Set(list)].sort();
    }
}
